import {
  getFallbackJurisdictionPackage,
  intakePanelConfigFromPackage,
} from '../../data/sfAdvisorIntake';

const clone = (x) => JSON.parse(JSON.stringify(x));

const threePanelSupport = {
  schemaVersion: 'app/v2',
  id: 'app_tpl_support',
  name: 'Support console (template)',
  description: 'Table + chat + summary — bind flows on the Layout tab.',
  layout: 'three-panel',
  chatPosition: 'center',
  theme: { themeId: 'ocean' },
  panels: [
    {
      id: 'left_table',
      type: 'component',
      componentType: 'table',
      position: 'left',
      order: 0,
      outputKey: 'table_data',
      source: {},
    },
    {
      id: 'center_chat',
      type: 'chat',
      position: 'center',
      order: 0,
      fixed: true,
    },
    {
      id: 'right_summary',
      type: 'summary',
      position: 'right',
      order: 0,
    },
  ],
};

const twoPanelData = {
  schemaVersion: 'app/v2',
  id: 'app_tpl_data',
  name: 'Data wall (template)',
  layout: 'two-panel',
  chatPosition: 'center',
  theme: { themeId: 'slate' },
  panels: [
    {
      id: 'left_chart',
      type: 'component',
      componentType: 'chart',
      position: 'left',
      order: 0,
      outputKey: 'chart_data',
      config: { chartType: 'bar' },
      source: {},
    },
    {
      id: 'center_chat',
      type: 'chat',
      position: 'center',
      order: 0,
      fixed: true,
    },
  ],
};

const minimalChat = {
  schemaVersion: 'app/v2',
  id: 'app_tpl_chat',
  name: 'Chat only (template)',
  layout: 'three-panel',
  chatPosition: 'center',
  panels: [
    {
      id: 'center_chat',
      type: 'chat',
      position: 'center',
      order: 0,
      fixed: true,
    },
  ],
};

const permitAdvisor = {
  schemaVersion: 'app/v2',
  id: 'app_tpl_permit',
  name: 'SF application advisor',
  description: 'Studio App for permit vs planning vs license — form + map intake, checklist, and summary (CLAW if_advise_application_type).',
  layout: 'two-panel',
  chatPosition: 'header',
  theme: { themeId: 'ocean', primaryColor: '#1976d2' },
  defaultFlowId: '',
  metadata: {
    interviewFlow: {
      playbook: 'permits-sf',
      jurisdictionId: 'san-francisco',
      helpPath: '/studio/help/permits',
      claw: {
        skill: 'if-sf-application-advisor',
        tools: [
          'if_lookup_sf_parcel',
          'if_check_sf_land_use',
          'if_advise_application_type',
          'if_scope_sf_requirements',
          'if_estimate_sf_fees',
          'builtin.web_search',
          'if_export_panel_json',
        ],
        demonstrates: ['skills', 'capabilities', 'agent-tool-loop'],
      },
      sharedChatHistoryAcrossPanels: true,
      persistSharedChatHistory: true,
      restoreSharedChatHistoryOnLoad: true,
      phases: [
        { id: 'advise', label: '1 · Project', order: 0 },
        { id: 'checklist', label: '2 · Applications', order: 1 },
        { id: 'next-steps', label: '3 · Summary', order: 2 },
      ],
      initialPhaseId: 'advise',
      advancePhaseOnAdvise: 'checklist',
    },
  },
  panels: [
    {
      id: 'left_intake',
      type: 'component',
      componentType: 'form',
      position: 'left',
      order: 0,
      outputKey: 'form_prefill',
      config: {
        interviewPhase: 'advise',
        ...intakePanelConfigFromPackage(getFallbackJurisdictionPackage('san-francisco')),
      },
      source: { outputPath: 'result.form_prefill' },
    },
    {
      id: 'left_checklist',
      type: 'component',
      componentType: 'table',
      position: 'left',
      order: 0,
      outputKey: 'table_data',
      config: {
        interviewPhase: 'checklist',
        title: 'Applications to consider',
      },
      source: { outputPath: 'result.checklist', bindings: [
        { targetKey: 'rows', sourcePath: 'result.checklist.rows', required: true },
        { targetKey: 'columns', sourcePath: 'result.checklist.columns', required: false },
      ] },
    },
    {
      id: 'left_summary',
      type: 'component',
      componentType: 'rich_text',
      position: 'left',
      order: 0,
      outputKey: 'rich_text',
      config: {
        interviewPhase: 'next-steps',
        title: 'Project summary',
        emptyMessage: 'Run advise from the Project step to build the summary.',
      },
      source: {
        outputPath: 'result.rich_text',
        bindings: [{ targetKey: 'content', sourcePath: 'result.rich_text.content', required: false }],
      },
    },
    {
      id: 'right_map',
      type: 'component',
      componentType: 'map',
      position: 'right',
      order: 0,
      outputKey: 'map_markers',
      config: {
        title: 'Parcel & clearance',
        interactivePick: true,
        showClearanceLegend: true,
        defaultZoom: 12,
        defaultCenter: { lat: 37.7749, lng: -122.4194 },
        clearanceColors: getFallbackJurisdictionPackage('san-francisco').intake.clearanceColors,
      },
      source: {
        outputPath: 'result.map_markers',
        bindings: [
          { targetKey: 'center', sourcePath: 'result.map_markers.center', required: false },
          { targetKey: 'markers', sourcePath: 'result.map_markers.markers', required: false },
          { targetKey: 'zoom', sourcePath: 'result.map_markers.zoom', required: false },
          { targetKey: 'clearance', sourcePath: 'result.map_markers.clearance', required: false },
        ],
      },
    },
    {
      id: 'right_parcel',
      type: 'component',
      componentType: 'rich_text',
      position: 'right',
      order: 1,
      outputKey: 'rich_text',
      config: {
        interviewPhase: ['advise', 'checklist'],
        title: 'Address validation & constraints',
        emptyMessage: 'Enter or pin an address — we validate the parcel and list zoning / historic / flood / slope constraints here.',
      },
      source: {
        outputPath: 'result.rich_text',
        bindings: [{ targetKey: 'content', sourcePath: 'result.rich_text.content', required: false }],
      },
    },
  ],
};

const intakeAssistant = {
  schemaVersion: 'app/v2',
  id: 'app_tpl_intake',
  name: 'Intake assistant',
  description: 'Capture intake details with a form, live notes, and summary guidance.',
  layout: 'three-panel',
  chatPosition: 'center',
  theme: { themeId: 'slate' },
  metadata: {
    interviewFlow: {
      sharedChatHistoryAcrossPanels: true,
      phases: [
        { id: 'triage', label: 'Triage', order: 0 },
        { id: 'details', label: 'Details', order: 1 },
        { id: 'followup', label: 'Follow-up', order: 2 },
      ],
    },
  },
  panels: [
    {
      id: 'left_form',
      type: 'component',
      componentType: 'form',
      position: 'left',
      order: 0,
      outputKey: 'form_prefill',
      config: {
        formFields: [
          { id: 'full_name', type: 'text', label: 'Full name', required: true },
          { id: 'email', type: 'email', label: 'Email', required: true },
          { id: 'notes', type: 'textarea', label: 'Notes' },
        ],
      },
      source: { outputPath: 'result.form_prefill' },
    },
    { id: 'center_chat', type: 'chat', position: 'center', order: 0, fixed: true },
    {
      id: 'right_notes',
      type: 'component',
      componentType: 'list',
      position: 'right',
      order: 0,
      outputKey: 'list_data',
      source: { outputPath: 'result.notes', bindings: [{ targetKey: 'items', sourcePath: 'result.notes.items', required: true }] },
    },
  ],
};

const customerSupport = {
  schemaVersion: 'app/v2',
  id: 'app_tpl_customer_support',
  name: 'Customer support workspace',
  description: 'Case queue, assistant chat, and KPI sidebar for support teams.',
  layout: 'three-panel',
  chatPosition: 'center',
  theme: { themeId: 'midnight' },
  panels: [
    {
      id: 'left_cases',
      type: 'component',
      componentType: 'table',
      position: 'left',
      order: 0,
      outputKey: 'table_data',
      source: { outputPath: 'result.cases' },
    },
    { id: 'center_chat', type: 'chat', position: 'center', order: 0, fixed: true },
    {
      id: 'right_stats',
      type: 'component',
      componentType: 'stats',
      position: 'right',
      order: 0,
      outputKey: 'stats_data',
      source: { outputPath: 'result.kpis', bindings: [{ targetKey: 'items', sourcePath: 'result.kpis.items', required: true }] },
    },
  ],
};

const candidateScreeningConsole = {
  schemaVersion: 'app/v2',
  id: 'app_tpl_screening',
  name: 'Candidate screening console',
  description: 'Screening table, shared chat, and rubric detail — bind a flow that emits result.screening.',
  layout: 'three-panel',
  chatPosition: 'center',
  theme: { themeId: 'forest' },
  metadata: {
    interviewFlow: {
      playbook: 1,
      sharedChatHistoryAcrossPanels: true,
      persistSharedChatHistory: true,
      handoff: {
        companionBundleId: 'interview-scorecard',
        copyFields: ['candidateName', 'roleApplied', 'screeningBucket', 'screeningScore'],
        note: 'When bucket is pass, open the scorecard bundle and paste handoff fields into the feedback form.',
      },
      previewPanelOutputs: {
        left_queue: {
          screening: {
            queue: {
              columns: [
                { field: 'candidate', headerName: 'Candidate', width: 160 },
                { field: 'role', headerName: 'Role', width: 140 },
                { field: 'bucket', headerName: 'Bucket', width: 100 },
                { field: 'score', headerName: 'Score', width: 80 },
                { field: 'nextStep', headerName: 'Next', width: 120 },
              ],
              rows: [
                { id: 'r1', candidate: 'Jordan Lee', role: 'Senior backend', bucket: 'pass', score: 82, nextStep: 'scorecard' },
                { id: 'r2', candidate: 'Sam Rivera', role: 'Platform eng', bucket: 'hold', score: 61, nextStep: 'review' },
              ],
            },
          },
        },
        right_detail: {
          screening: {
            detail: {
              content: '## Screening snapshot\n\n**Jordan Lee** · **pass** · score **82**\n\n- Backend & distributed systems match role summary.\n- Sponsorship: not required.\n\n**Next:** open **Post-interview summary & scorecard** bundle and copy handoff fields.',
            },
          },
        },
      },
    },
  },
  panels: [
    {
      id: 'left_queue',
      type: 'component',
      componentType: 'table',
      position: 'left',
      order: 0,
      outputKey: 'table_data',
      source: {
        outputPath: 'result.screening.queue',
        bindings: [
          { targetKey: 'rows', sourcePath: 'result.screening.queue.rows', required: true },
          { targetKey: 'columns', sourcePath: 'result.screening.queue.columns', required: false },
        ],
      },
    },
    { id: 'center_chat', type: 'chat', position: 'center', order: 0, fixed: true },
    {
      id: 'right_detail',
      type: 'component',
      componentType: 'rich_text',
      position: 'right',
      order: 0,
      outputKey: 'rich_text',
      source: {
        outputPath: 'result.screening.detail',
        bindings: [{ targetKey: 'content', sourcePath: 'result.screening.detail.content', required: true }],
      },
    },
  ],
};

const interviewScorecardConsole = {
  schemaVersion: 'app/v2',
  id: 'app_tpl_scorecard',
  name: 'Interview scorecard console',
  description: 'Dimension table, shared chat, and manager summary — bind a flow that emits result.summary + result.scorecard.',
  layout: 'three-panel',
  chatPosition: 'center',
  theme: { themeId: 'forest' },
  metadata: {
    interviewFlow: {
      playbook: 3,
      sharedChatHistoryAcrossPanels: true,
      persistSharedChatHistory: true,
      handoffFrom: 'candidate-screening',
      previewPanelOutputs: {
        left_dimensions: {
          scorecard: {
            table: {
              columns: [
                { field: 'dimension', headerName: 'Dimension', width: 180 },
                { field: 'score', headerName: 'Score', width: 80 },
                { field: 'evidence', headerName: 'Evidence', width: 280 },
              ],
              rows: [
                { id: 'd1', dimension: 'Technical depth', score: '5/5', evidence: 'Strong system design on idempotency and failure modes.' },
                { id: 'd2', dimension: 'Communication', score: '4/5', evidence: 'Clear trade-off discussion; minor rambling on edge cases.' },
              ],
            },
          },
        },
        right_summary: {
          summary: 'Jordan Lee demonstrated senior-level backend depth in the payments reliability discussion. Communication was clear and structured. Recommend **hire** for Senior Backend Engineer with standard onboarding on front-end collaboration norms.',
        },
      },
    },
  },
  panels: [
    {
      id: 'left_dimensions',
      type: 'component',
      componentType: 'table',
      position: 'left',
      order: 0,
      outputKey: 'table_data',
      source: {
        outputPath: 'result.scorecard.table',
        bindings: [
          { targetKey: 'rows', sourcePath: 'result.scorecard.table.rows', required: true },
          { targetKey: 'columns', sourcePath: 'result.scorecard.table.columns', required: false },
        ],
      },
    },
    { id: 'center_chat', type: 'chat', position: 'center', order: 0, fixed: true },
    {
      id: 'right_summary',
      type: 'component',
      componentType: 'rich_text',
      position: 'right',
      order: 0,
      outputKey: 'rich_text',
      source: {
        outputPath: 'result.summary',
        bindings: [{ targetKey: 'content', sourcePath: 'result.summary', required: true }],
      },
    },
  ],
};

const recruitingPilotConsole = {
  schemaVersion: 'app/v2',
  id: 'app_tpl_recruiting_pilot',
  name: 'Recruiting pilot console',
  description: 'Single app with screening and scorecard phases, shared chat, intake forms, and in-app handoff.',
  layout: 'three-panel',
  chatPosition: 'center',
  theme: { themeId: 'forest' },
  metadata: {
    interviewFlow: {
      playbook: '1+3',
      sharedChatHistoryAcrossPanels: true,
      persistSharedChatHistory: true,
      initialPhaseId: 'screening',
      phases: [
        { id: 'screening', label: 'Screening', order: 0 },
        { id: 'scorecard', label: 'Scorecard', order: 1 },
      ],
      phaseFlows: {},
      handoff: {
        targetPhaseId: 'scorecard',
        copyFields: ['candidateName', 'roleApplied', 'screeningBucket', 'screeningScore'],
        note: 'Click a pass row in the screening queue to switch to scorecard with prefill.',
      },
      previewPanelOutputs: {
        ...(candidateScreeningConsole.metadata?.interviewFlow?.previewPanelOutputs || {}),
        ...(interviewScorecardConsole.metadata?.interviewFlow?.previewPanelOutputs || {}),
      },
    },
  },
  panels: [
    {
      id: 'left_queue',
      type: 'component',
      componentType: 'table',
      position: 'left',
      order: 0,
      outputKey: 'table_data',
      config: { interviewPhase: 'screening' },
      source: {
        outputPath: 'result.screening.queue',
        bindings: [
          { targetKey: 'rows', sourcePath: 'result.screening.queue.rows', required: true },
          { targetKey: 'columns', sourcePath: 'result.screening.queue.columns', required: false },
        ],
      },
    },
    {
      id: 'left_dimensions',
      type: 'component',
      componentType: 'table',
      position: 'left',
      order: 0,
      outputKey: 'table_data',
      config: { interviewPhase: 'scorecard' },
      source: {
        outputPath: 'result.scorecard.table',
        bindings: [
          { targetKey: 'rows', sourcePath: 'result.scorecard.table.rows', required: true },
          { targetKey: 'columns', sourcePath: 'result.scorecard.table.columns', required: false },
        ],
      },
    },
    { id: 'center_chat', type: 'chat', position: 'center', order: 0, fixed: true },
    {
      id: 'right_detail',
      type: 'component',
      componentType: 'rich_text',
      position: 'right',
      order: 0,
      outputKey: 'rich_text',
      config: { interviewPhase: 'screening' },
      source: {
        outputPath: 'result.screening.detail',
        bindings: [{ targetKey: 'content', sourcePath: 'result.screening.detail.content', required: true }],
      },
    },
    {
      id: 'right_summary',
      type: 'component',
      componentType: 'rich_text',
      position: 'right',
      order: 0,
      outputKey: 'rich_text',
      config: { interviewPhase: 'scorecard' },
      source: {
        outputPath: 'result.summary',
        bindings: [{ targetKey: 'content', sourcePath: 'result.summary', required: true }],
      },
    },
  ],
};

const leadQualification = {
  schemaVersion: 'app/v2',
  id: 'app_tpl_leads',
  name: 'Lead qualification',
  description: 'Qualify leads with shared conversation history, scores, and next actions.',
  layout: 'three-panel',
  chatPosition: 'center',
  theme: { themeId: 'forest' },
  metadata: {
    interviewFlow: {
      sharedChatHistoryAcrossPanels: true,
      persistSharedChatHistory: true,
    },
  },
  panels: [
    {
      id: 'left_pipeline',
      type: 'component',
      componentType: 'timeline',
      position: 'left',
      order: 0,
      outputKey: 'timeline_data',
      source: { outputPath: 'result.timeline', bindings: [{ targetKey: 'events', sourcePath: 'result.timeline.events', required: true }] },
    },
    { id: 'center_chat', type: 'chat', position: 'center', order: 0, fixed: true },
    {
      id: 'right_score',
      type: 'component',
      componentType: 'stats',
      position: 'right',
      order: 0,
      outputKey: 'stats_data',
      source: { outputPath: 'result.scorecard', bindings: [{ targetKey: 'items', sourcePath: 'result.scorecard.items', required: true }] },
    },
  ],
};

export const STUDIO_V2_APP_TEMPLATES = [
  {
    id: 'permit-advisor',
    label: 'SF application advisor',
    description: permitAdvisor.description || 'Checklist + chat + summary for permit | planning | license.',
    category: 'vertical',
    tags: ['government', 'permits', 'planning', 'license', 'claw', 'san-francisco', 'checklist'],
    definition: clone(permitAdvisor),
  },
  {
    id: 'intake-assistant',
    label: 'Intake assistant',
    description: intakeAssistant.description || 'Form-driven intake with chat and notes.',
    category: 'vertical',
    tags: ['forms', 'intake'],
    definition: clone(intakeAssistant),
  },
  {
    id: 'customer-support',
    label: 'Customer support workspace',
    description: customerSupport.description || 'Cases, chat, and KPI sidebar.',
    category: 'vertical',
    tags: ['support', 'table'],
    definition: clone(customerSupport),
  },
  {
    id: 'lead-qualification',
    label: 'Lead qualification',
    description: leadQualification.description || 'Pipeline timeline with scoring panels.',
    category: 'vertical',
    tags: ['sales', 'crm'],
    definition: clone(leadQualification),
  },
  {
    id: 'candidate-screening-console',
    label: 'Candidate screening console',
    description: candidateScreeningConsole.description || 'Table + chat + rubric detail for result.screening.',
    category: 'vertical',
    tags: ['recruiting', 'screening', 'talent', 'table'],
    definition: clone(candidateScreeningConsole),
  },
  {
    id: 'interview-scorecard-console',
    label: 'Interview scorecard console',
    description: interviewScorecardConsole.description || 'Dimension table + chat + manager summary for result.scorecard.',
    category: 'vertical',
    tags: ['recruiting', 'scorecard', 'talent', 'table'],
    definition: clone(interviewScorecardConsole),
  },
  {
    id: 'recruiting-pilot-console',
    label: 'Recruiting pilot console (#1 + #3)',
    description: recruitingPilotConsole.description || 'Screening + scorecard phases in one app with shared chat.',
    category: 'vertical',
    tags: ['recruiting', 'pilot', 'screening', 'scorecard'],
    definition: clone(recruitingPilotConsole),
  },
  {
    id: 'three-support',
    label: 'Three-panel · table + chat + summary',
    description: 'Classic console layout — bind your flows on the Layout tab.',
    category: 'layout',
    tags: ['starter', 'dashboard'],
    definition: clone(threePanelSupport),
  },
  {
    id: 'two-data',
    label: 'Two-panel · chart + chat',
    description: 'Chart-led workspace with centered assistant.',
    category: 'layout',
    tags: ['analytics', 'compact'],
    definition: clone(twoPanelData),
  },
  {
    id: 'chat-only',
    label: 'Chat-only shell',
    description: minimalChat.description || 'Single chat column — fastest shell to attach a flow.',
    category: 'layout',
    tags: ['minimal', 'chat'],
    definition: clone(minimalChat),
  },
];

export function mergeAppTemplateIntoCurrent(currentText, templateDef, templateId) {
  let cur;
  try {
    cur = JSON.parse(currentText || '{}');
  } catch {
    return { error: 'Current app JSON is invalid.' };
  }
  if (!cur || typeof cur !== 'object') return { error: 'Invalid app object.' };
  const next = {
    ...cur,
    id: cur.id,
    name: templateDef.name || cur.name,
    description: templateDef.description ?? cur.description,
    layout: templateDef.layout ?? cur.layout,
    chatPosition: templateDef.chatPosition ?? cur.chatPosition,
    theme: templateDef.theme && typeof templateDef.theme === 'object'
      ? { ...templateDef.theme }
      : (cur.theme || {}),
    panels: Array.isArray(templateDef.panels) ? clone(templateDef.panels) : cur.panels,
    metadata: {
      ...(cur.metadata && typeof cur.metadata === 'object' ? cur.metadata : {}),
      lastTemplateId: templateId,
      lastTemplateAt: new Date().toISOString(),
    },
  };
  return { text: JSON.stringify(next, null, 2) };
}
